/**
 * @file NavbarContext.tsx
 * @description Context for managing the state of the navbar, specifically the login dropdown.
 * Provides a way to open the login dropdown and check its state across components.
 */

import React, { createContext, useContext, useState, useCallback } from "react";

interface NavbarContextType {
  openLoginDropdown: () => void;
  isLoginDropdownOpen: boolean;
}

const NavbarContext = createContext<NavbarContextType | undefined>(undefined);

/**
 * Provider component for the NavbarContext.
 * Manages the state of the login dropdown and provides methods to interact with it.
 *
 * @param {React.PropsWithChildren<{}>} props - The children components to be wrapped by the provider.
 * @returns {JSX.Element} The NavbarContext provider component.
 */
export const NavbarProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);

  /**
   * Opens the login dropdown.
   */
  const openLoginDropdown = useCallback(() => {
    setIsLoginDropdownOpen(true);
  }, []);

  return (
    <NavbarContext.Provider value={{ openLoginDropdown, isLoginDropdownOpen }}>
      {children}
    </NavbarContext.Provider>
  );
};

/**
 * Custom hook to use the NavbarContext.
 *
 * @returns {NavbarContextType} The NavbarContext value.
 * @throws {Error} If used outside of a NavbarProvider.
 */
export const useNavbarContext = () => {
  const context = useContext(NavbarContext);
  if (context === undefined) {
    throw new Error("useNavbarContext must be used within a NavbarProvider");
  }
  return context;
};
